import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isNativeApp } from "@/lib/platform";
import { Star, X } from "lucide-react";

/**
 * ReviewPromptModal — on day 5+ of the 7-day Pro trial, ask the captain
 * (once) to leave a review. Trial captains are Pro users mid-window who
 * have used the app long enough to have an opinion.
 *
 * Trigger conditions (ALL must hold):
 *   - user.subscription_status === "free"
 *   - user.plan_tier === "pro" (mid-trial)
 *   - user.pro_until is set and expires within 2 days (≙ trial day 5-7)
 *   - user.review_prompted_at is null
 *
 * Review flow:
 *   - Native iOS/Android: opens the true in-app review sheet via
 *     @capacitor-community/in-app-review — SKStoreReviewController on iOS,
 *     Play In-App Review API on Android. Captain never leaves the app.
 *   - Web / native fallback: opens the App Store or Play Store listing.
 *
 * Marks the user as prompted immediately on either "Leave a review" OR
 * "Maybe later", so we never re-nag. Persisted via PATCH /auth/me.
 */

const APP_STORE_URL = "https://apps.apple.com/app/id6770087383?action=write-review";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.bosunsmate&showAllReviews=true";

// Local-only guard: once we've marked the server, don't re-show in the same
// tab session even if user reload happens before the /auth/me refresh.
const SESSION_SEEN_KEY = "bm_review_prompt_seen";

function daysLeft(iso) {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function storeUrlForClient() {
    if (typeof navigator === "undefined") return APP_STORE_URL;
    const ua = (navigator.userAgent || "").toLowerCase();
    if (ua.includes("android")) return PLAY_STORE_URL;
    return APP_STORE_URL;
}

/**
 * Fire the native in-app review sheet. Dynamic import so the web bundle
 * never touches the native module. Returns true if the native prompt was
 * successfully invoked, false on web or on any failure (so the caller can
 * fall back to a store URL).
 */
async function requestNativeReview() {
    if (!isNativeApp()) return false;
    try {
        const mod = await import("@capacitor-community/in-app-review");
        await mod.InAppReview.requestReview();
        return true;
    } catch (e) {
        console.warn("[review-prompt] native requestReview failed, falling back", e);
        return false;
    }
}

export default function ReviewPromptModal() {
    const { user, setUser } = useAuth();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const shouldShow = useMemo(() => {
        if (!user) return false;
        if (user.subscription_status !== "free") return false;
        if (user.plan_tier !== "pro") return false;
        if (!user.pro_until) return false;
        if (user.review_prompted_at) return false;
        if (sessionStorage.getItem(SESSION_SEEN_KEY) === "1") return false;
        const days = daysLeft(user.pro_until);
        // Day 5-7 of a 7-day trial = days remaining in 0..2 (inclusive at 2,
        // still valid at 1). Exclude 0 (trial fully lapsed).
        return days > 0 && days <= 2;
    }, [user]);

    useEffect(() => {
        if (!shouldShow) return;
        // Gentle delay so we don't ambush the captain the second they log in
        const t = setTimeout(() => setOpen(true), 3500);
        return () => clearTimeout(t);
    }, [shouldShow]);

    const markPromptedOnServer = async () => {
        const iso = new Date().toISOString();
        sessionStorage.setItem(SESSION_SEEN_KEY, "1");
        try {
            const { data } = await api.patch("/auth/me", { review_prompted_at: iso });
            if (data && setUser) setUser(data);
        } catch {
            // Non-fatal: session guard still stops re-showing this tab.
        }
    };

    const leaveReview = async () => {
        if (busy) return;
        setBusy(true);
        await markPromptedOnServer();
        try {
            // Try the native in-app review sheet first (iOS SKStoreReviewController
            // or Play In-App Review). If we're on web OR the plugin fails, fall
            // back to opening the store listing in a new tab.
            const nativeOk = await requestNativeReview();
            if (!nativeOk) {
                const url = storeUrlForClient();
                window.open(url, "_blank", "noopener,noreferrer");
            }
        } finally {
            setBusy(false);
            setOpen(false);
        }
    };

    const later = async () => {
        if (busy) return;
        setBusy(true);
        await markPromptedOnServer();
        setBusy(false);
        setOpen(false);
    };

    if (!open) return null;

    return (
        <div
            data-testid="review-prompt-modal"
            role="dialog"
            aria-label="Leave a review"
            onClick={(e) => { if (e.target === e.currentTarget && !busy) later(); }}
            style={{
                position: "fixed", inset: 0, zIndex: 120,
                background: "rgba(4, 12, 22, 0.72)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 20,
            }}
        >
            <div
                className="bm-card"
                style={{
                    maxWidth: 440, width: "100%", padding: "26px 24px 24px",
                    position: "relative",
                    background: "linear-gradient(135deg, rgba(212, 181, 114, 0.08), rgba(212, 181, 114, 0.02))",
                    border: "1px solid rgba(212, 181, 114, 0.35)",
                }}
            >
                <button
                    data-testid="review-prompt-close"
                    onClick={later}
                    aria-label="Close"
                    disabled={busy}
                    style={{
                        position: "absolute", top: 12, right: 12,
                        background: "none", border: "none", cursor: "pointer",
                        color: "var(--bm-text-muted)", padding: 6, display: "flex",
                    }}
                >
                    <X size={16} />
                </button>
                <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 12 }}>
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Star key={i} size={22} color="var(--bm-gold)" style={{ fill: "var(--bm-gold)" }} />
                    ))}
                </div>
                <h2 className="bm-serif" style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 500, textAlign: "center" }}>
                    Enjoying Bosun&apos;s Mate?
                </h2>
                <p style={{ margin: 0, color: "var(--bm-text-muted)", fontSize: 14, textAlign: "center", lineHeight: 1.55 }}>
                    You&apos;ve been on the water with us for a few days. If it&apos;s helping you keep the boat sharp, would you leave a quick review? Every star helps other captains find the app.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 22 }}>
                    <button
                        data-testid="review-prompt-cta"
                        onClick={leaveReview}
                        disabled={busy}
                        className="bm-btn bm-btn-gold"
                        style={{ padding: "12px 18px", fontSize: 14, opacity: busy ? 0.6 : 1 }}
                    >
                        Leave a review
                    </button>
                    <button
                        data-testid="review-prompt-later"
                        onClick={later}
                        disabled={busy}
                        style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: "var(--bm-text-muted)", fontSize: 13, padding: "6px 0",
                        }}
                    >
                        Maybe later
                    </button>
                </div>
            </div>
        </div>
    );
}
